import React, { useEffect, useRef, useState } from 'react';
import { useMetricType } from 'hooks/useMetricType';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as S from '../containers/PropertyPanel/PropertyPanel.styles';
import {
  convertToCurrentMetric,
  convertToDefaultMetric,
} from 'util/conversions';
import { parseNumberInputValue } from 'util/parseNumberInput';

/**
 * Interface for the NumberInput component
 */
interface NumberInputInterface {
  value: number | undefined;
  setValue(val: number | undefined): void;
  label: string;
  marginRight?: number;
  conversionType?: string;
  disabled?: boolean;
  optional?: boolean;
  size?: number;
  error?: boolean;
  step?: string;
  min?: number;
  max?: number;
}

const StepperAdornment = styled(InputAdornment)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  bottom: theme.spacing(0.75),
  height: 32,
  maxHeight: 'none',
  margin: 0,
}));

const StepperButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: 32,
  '& .MuiIconButton-root': {
    width: 20,
    height: 16,
    padding: 0,
    borderRadius: 2,
    color: theme.palette.grey[700],
  },
  '& .MuiIconButton-root.Mui-disabled': {
    color: theme.palette.grey[500],
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
}));

const getDecimalPlaces = (value: number | string | undefined) => {
  if (typeof value === 'undefined') {
    return 0;
  }

  const decimalPart = String(value).replace(',', '.').split('.')[1];

  return decimalPart ? decimalPart.length : 0;
};

const STEP_REPEAT_DELAY_MS = 400;
const STEP_REPEAT_INTERVAL_MS = 75;

/**
 * Component used in property panels for filling out a number
 * @param value - Entered value in the number input field
 * @param setValue - Used to set the value on each change in the NumberInput
 * @param label - Label of the Number input
 * @param marginRight - Set a margin on the right side of the NumberInput
 * @param disabled - If true the NumberInput will be greyed out
 * @param conversionType - Type of conversion to be made on the value, size, capacity, density or volume
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 */
export const NumberInput = ({
  value,
  setValue,
  label,
  marginRight,
  disabled,
  conversionType,
  optional,
  size,
  error,
  step,
  min,
  max,
}: NumberInputInterface) => {
  const metricType = useMetricType();

  const displayValue = conversionType
    ? convertToCurrentMetric({
        value: value,
        metricType,
        conversionType: conversionType,
        decimals: disabled ? undefined : 5,
      })
    : value;

  const formattedDisplayValue =
    displayValue !== undefined ? String(displayValue) : '';
  const [inputValue, setInputValue] = useState(formattedDisplayValue);
  const [isEditing, setIsEditing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputValueRef = useRef(inputValue);
  const displayValueRef = useRef(displayValue);
  const stepRepeatDelayRef = useRef<number | undefined>(undefined);
  const stepRepeatIntervalRef = useRef<number | undefined>(undefined);

  inputValueRef.current = inputValue;
  displayValueRef.current = displayValue;

  const clampValue = (nextValue: number) => {
    const minValue = min ?? 0;
    const valueWithinMin = Math.max(nextValue, minValue);

    return max !== undefined ? Math.min(valueWithinMin, max) : valueWithinMin;
  };

  const setParsedValue = (parsedValue: number | undefined) => {
    if (typeof parsedValue === 'undefined') {
      setValue(undefined);
      return;
    }

    const nextValue = clampValue(parsedValue);

    if (typeof conversionType !== 'undefined') {
      setValue(
        convertToDefaultMetric({
          value: nextValue,
          metricType,
          conversionType: conversionType,
        }),
      );
    } else {
      setValue(nextValue);
    }
  };

  const getStepValue = () => {
    const parsedStep = step ? parseNumberInputValue(step) : Number.NaN;

    return typeof parsedStep === 'number' && parsedStep > 0 ? parsedStep : 1;
  };

  const formatSteppedValue = (
    nextValue: number,
    stepValue: number,
    currentInputValue: string,
  ) => {
    const decimalPlaces = Math.min(
      Math.max(
        getDecimalPlaces(currentInputValue),
        getDecimalPlaces(stepValue),
      ),
      10,
    );

    return String(Number(nextValue.toFixed(decimalPlaces)));
  };

  const stepInputValue = (direction: 1 | -1) => {
    const stepValue = getStepValue();
    const currentInputValue = inputValueRef.current;
    const parsedInputValue = parseNumberInputValue(currentInputValue);
    const currentValue =
      typeof parsedInputValue === 'number'
        ? parsedInputValue
        : displayValueRef.current;
    const baseValue =
      typeof currentValue === 'number'
        ? currentValue
        : direction > 0
          ? (min ?? 0) - stepValue
          : (min ?? 0) + stepValue;
    const nextValue = clampValue(baseValue + direction * stepValue);
    const nextInputValue = formatSteppedValue(
      nextValue,
      stepValue,
      currentInputValue,
    );

    setIsEditing(true);
    inputValueRef.current = nextInputValue;
    setInputValue(nextInputValue);
    setParsedValue(nextValue);
  };

  const stopStepRepeat = () => {
    if (typeof stepRepeatDelayRef.current !== 'undefined') {
      window.clearTimeout(stepRepeatDelayRef.current);
      stepRepeatDelayRef.current = undefined;
    }

    if (typeof stepRepeatIntervalRef.current !== 'undefined') {
      window.clearInterval(stepRepeatIntervalRef.current);
      stepRepeatIntervalRef.current = undefined;
    }
  };

  const startStepRepeat = (
    e: React.MouseEvent<HTMLButtonElement>,
    direction: 1 | -1,
  ) => {
    e.preventDefault();

    if (disabled) {
      return;
    }

    stopStepRepeat();
    stepInputValue(direction);
    stepRepeatDelayRef.current = window.setTimeout(() => {
      stepRepeatIntervalRef.current = window.setInterval(() => {
        stepInputValue(direction);
      }, STEP_REPEAT_INTERVAL_MS);
    }, STEP_REPEAT_DELAY_MS);
  };

  const handleStepperClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    direction: 1 | -1,
  ) => {
    if (e.detail === 0) {
      stepInputValue(direction);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      inputValueRef.current = formattedDisplayValue;
      setInputValue(formattedDisplayValue);
    }
  }, [formattedDisplayValue, isEditing]);

  useEffect(
    () => () => {
      if (typeof stepRepeatDelayRef.current !== 'undefined') {
        window.clearTimeout(stepRepeatDelayRef.current);
      }

      if (typeof stepRepeatIntervalRef.current !== 'undefined') {
        window.clearInterval(stepRepeatIntervalRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    const handleWheel = (e: WheelEvent) => {
      const hasInputFocus =
        inputRef.current?.ownerDocument.activeElement === inputRef.current;

      if (disabled || !hasInputFocus || e.deltaY === 0) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      stepInputValue(e.deltaY < 0 ? 1 : -1);
    };

    wrapper.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    });

    return () => {
      wrapper.removeEventListener('wheel', handleWheel, {
        capture: true,
      });
    };
  });

  return (
    <S.FormWrapper ref={wrapperRef} spaceRight={marginRight} size={size}>
      <FormControl variant='filled' fullWidth>
        <TextField
          label={label}
          variant='filled'
          value={inputValue}
          disabled={disabled}
          inputRef={inputRef}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const nextInputValue = e.target.value;
            inputValueRef.current = nextInputValue;
            setInputValue(nextInputValue);

            // Convert the value to Metric square again before putting it in the state
            const parsedValue = parseNumberInputValue(nextInputValue);

            if (nextInputValue.trim().length === 0) {
              setValue(undefined);
              return;
            }

            if (typeof parsedValue === 'number') {
              setParsedValue(parsedValue);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              stepInputValue(1);
            }

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              stepInputValue(-1);
            }
          }}
          onFocus={(e) => {
            setIsEditing(true);
            e.target.select();
          }}
          onBlur={() => {
            stopStepRepeat();
            setIsEditing(false);
            inputValueRef.current = formattedDisplayValue;
            setInputValue(formattedDisplayValue);
          }}
          error={(!optional && typeof value === 'undefined') || error}
          color='info'
          slotProps={{
            input: {
              sx: {
                '& .MuiFilledInput-input': {
                  paddingRight: '36px',
                },
              },
              endAdornment: (
                <StepperAdornment position='end'>
                  <StepperButtons>
                    <IconButton
                      aria-label={`Increase ${label}`}
                      disabled={disabled}
                      size='small'
                      onClick={(e) => handleStepperClick(e, 1)}
                      onMouseDown={(e) => startStepRepeat(e, 1)}
                      onMouseLeave={stopStepRepeat}
                      onMouseUp={stopStepRepeat}
                    >
                      <KeyboardArrowUpIcon />
                    </IconButton>
                    <IconButton
                      aria-label={`Decrease ${label}`}
                      disabled={disabled}
                      size='small'
                      onClick={(e) => handleStepperClick(e, -1)}
                      onMouseDown={(e) => startStepRepeat(e, -1)}
                      onMouseLeave={stopStepRepeat}
                      onMouseUp={stopStepRepeat}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </StepperButtons>
                </StepperAdornment>
              ),
            },

            htmlInput: {
              inputMode: 'decimal',
              min: min !== undefined ? String(min) : '0',
              max: max !== undefined ? String(max) : undefined,
              step: step,
              pattern: '-?[0-9]*([.,][0-9]+)?',
              title: '',
            },
          }}
        />
      </FormControl>
    </S.FormWrapper>
  );
};
